import type { DieSides, MasterDie, Rarity } from './types';
import { AFFIX_COUNT } from '../data/machines';
import { rollAffixes } from './affixes';
import { createMasterDie } from './dice';
import { markCorrupted } from './gacha';
import { Rng, randomSeed } from './rng';
import { emptySave, type SaveData } from './save';
import { fullCampaignTree, type NodeLevels } from './skilltree';
import { ENDLESS_FROM } from './world';

/**
 * **Départ direct en mode infini** (v0.53.0, A103) — « Nouvelle partie »
 * propose, à côté de la campagne, une partie qui commence au **niveau 11**
 * avec **tout l'arbre de campagne** débloqué (`fullCampaignTree`, l'Abîme à 0
 * sauf *Artisan des dés*) et **sept d20 Inhabituels Corrupted de niveau 1** :
 * la dotation du GD (A103 « 7 dés inhabituels corrupted 20 » ; A106 : le 20
 * est le type de dé, pas le niveau — les dés tirés sont toujours au niveau 1,
 * A105). Fonctions pures (le `Rng` est passé ou tiré d'une graine).
 */

/** La dotation du départ infini (A103, A106) : sept d20 Inhabituels Corrupted de niveau 1. */
export const ENDLESS_START = {
  level: ENDLESS_FROM,
  diceCount: 7,
  dieSides: 20 as DieSides,
  dieRarity: 'uncommon' as Rarity,
  dieLevel: 1,
} as const;

/**
 * Les dés de la dotation : tirés comme à une *Corrupted GachaDice* de même
 * rareté (affixes de la rareté, sorts, nom, Matériau de l'arbre `levels`,
 * jamais Enrichi sans le nœud), au niveau `ENDLESS_START.dieLevel` (1, imposé
 * pour ne pas dépendre du tirage des machines).
 */
export function endlessStartDice(rng: Rng, levels: NodeLevels = fullCampaignTree()): MasterDie[] {
  const out: MasterDie[] = [];
  for (let i = 0; i < ENDLESS_START.diceCount; i++) {
    const affixes = rollAffixes(ENDLESS_START.dieSides, AFFIX_COUNT[ENDLESS_START.dieRarity] ?? 0, rng);
    const die = createMasterDie(ENDLESS_START.dieSides, ENDLESS_START.dieRarity, affixes, rng);
    out.push(markCorrupted(die, levels, rng, ENDLESS_START.dieLevel));
  }
  return out;
}

/**
 * Sauvegarde d'une partie neuve **en mode infini** : niveau 11, jour 1, en
 * Accalmie, or 0, arbre de campagne complet, les sept dés équipés dans l'ordre
 * (l'arbre complet donne sept emplacements ; `Game` ajuste au besoin), crédits
 * tenus pour vus (`creditsSeen`, donc retour au niveau 11 à la mort — A101) et
 * meilleur niveau 11 (portes de l'Abîme), lore tenu pour lu. Monde et boss du
 * niveau sont tirés par `Game` au chargement (`phases.ensureWorld`).
 */
export function endlessStartSave(seed = randomSeed()): SaveData {
  const rng = new Rng(seed);
  const save = emptySave(seed);
  const tree = fullCampaignTree();
  const dice = endlessStartDice(rng, tree);
  save.run = { ...save.run, phase: 'accalmie', level: ENDLESS_START.level, day: 1 };
  save.tree = tree;
  save.inventory = dice;
  save.equipped = dice.map((d) => d.id);
  save.stats = { ...save.stats, bestLevel: ENDLESS_START.level, creditsSeen: true, loreSeen: true };
  save.rngSeed = { seed: rng.seed, state: rng.state };
  return save;
}
